import styles from "./tag.module.css";

/**
 * Tags carry a semantic tone, not a colour.
 *
 * Each tone resolves to a container role and its paired on-* role, so the label
 * stays legible in light, dark and blue chrome without the caller thinking about
 * it. A tone is the whole choice: never set a background on a tag, or the label
 * colour and the background stop moving together.
 */
export type TagTone =
  "neutral" | "info" | "success" | "warning" | "danger" | "accent";

/** Tones the "auto" hash may pick from - the decorative ones only. */
const AUTO_TONES: ReadonlyArray<TagTone> = [
  "info",
  "accent",
  "success",
  "warning",
];

interface TagProps {
  label: string;
  display?: {
    tone?: TagTone | "auto";
  };
}

function toneFor(label: string, tone: TagTone | "auto"): TagTone {
  if (tone !== "auto") return tone;
  const firstCharCode = label.codePointAt(0) || 0;
  return AUTO_TONES[firstCharCode % AUTO_TONES.length];
}

function Tag({ label, display }: TagProps) {
  const tone = toneFor(label, display?.tone ?? "neutral");

  return (
    <span
      data-test-role="tag"
      data-tone={tone}
      className={styles.tag}
      style={{
        backgroundColor: `var(--pallet-role-${tone}-container)`,
        color: `var(--pallet-role-on-${tone}-container)`,
      }}
    >
      {label}
    </span>
  );
}

export default Tag;
